import { useEffect } from "react";

/**
 * Sets per-page SEO tags: title, description, canonical link, and injects JSON-LD.
 * Run in a page component's top-level. Cleans up JSON-LD on unmount.
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} opts.canonical - Absolute URL for the canonical link
 * @param {object} [opts.schema] - Optional JSON-LD object to inject
 * @param {string} [opts.schemaId] - Unique DOM id for the injected script (so we can remove it)
 */
export function usePageSEO({ title, description, canonical, schema, schemaId }) {
  useEffect(() => {
    if (title) document.title = title;

    if (description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute("content", description);
    }

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);

      // Update og:url too
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute("content", canonical);
    }

    let script;
    if (schema && schemaId) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = schemaId;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      if (script) {
        const existing = document.getElementById(schemaId);
        if (existing) existing.remove();
      }
    };
  }, []);
}
